import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Plus, Trash2, Edit3, Check, X, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { authHeaders, clearToken, checkAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JOB_TYPE_OPTIONS = [
  { value: "vast", label: "Vaste functie" },
  { value: "flexi", label: "Flexi-job" },
  { value: "student", label: "Student" },
  { value: "onderaannemer", label: "Onderaannemer" },
];

const emptyForm = {
  title: "",
  subtitle: "",
  type_job: "student",
  description: "",
  highlights: [""],
  tasks: [""],
  active: true,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const user = await checkAuth();
      if (!user) {
        navigate("/admin/login");
        return;
      }
      await loadVacancies();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVacancies = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/vacancies`, { headers: authHeaders() });
      setVacancies(data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    }
    setLoading(false);
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
    setError("");
  };

  const openEdit = (v) => {
    setForm({
      title: v.title || "",
      subtitle: v.subtitle || "",
      type_job: v.type_job || "student",
      description: v.description || "",
      highlights: v.highlights?.length ? v.highlights : [""],
      tasks: v.tasks?.length ? v.tasks : [""],
      active: v.active !== false,
    });
    setEditingId(v.id);
    setShowForm(true);
    setError("");
  };

  const updateList = (field, index, value) => {
    setForm((f) => {
      const list = [...f[field]];
      list[index] = value;
      return { ...f, [field]: list };
    });
  };

  const addListItem = (field) => setForm((f) => ({ ...f, [field]: [...f[field], ""] }));
  const removeListItem = (field, index) =>
    setForm((f) => ({ ...f, [field]: f[field].filter((_, i) => i !== index) }));

  const save = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      ...form,
      highlights: form.highlights.map((h) => h.trim()).filter(Boolean),
      tasks: form.tasks.map((t) => t.trim()).filter(Boolean),
    };
    if (!payload.title || !payload.description) {
      setError("Titel en beschrijving zijn verplicht.");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/vacancies/${editingId}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/vacancies`, payload, { headers: authHeaders() });
      }
      setShowForm(false);
      setEditingId(null);
      await loadVacancies();
    } catch (err) {
      setError(err.response?.data?.detail || "Er ging iets mis. Probeer opnieuw.");
    }
  };

  const toggleActive = async (v) => {
    try {
      await axios.put(`${API}/admin/vacancies/${v.id}`, { ...v, active: !v.active }, { headers: authHeaders() });
      await loadVacancies();
    } catch {}
  };

  const remove = async (v) => {
    if (!window.confirm(`Vacature "${v.title}" definitief verwijderen?`)) return;
    try {
      await axios.delete(`${API}/admin/vacancies/${v.id}`, { headers: authHeaders() });
      await loadVacancies();
    } catch {}
  };

  return (
    <div className="min-h-screen bg-cgsurface">
      {/* Top bar */}
      <div className="bg-cgdark text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-cgprimary font-semibold">Cargides admin</div>
            <div className="font-heading text-xl font-bold">Vacatures beheren</div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              className="hidden sm:inline-flex items-center gap-2 text-white/70 hover:text-white text-[12px] font-semibold tracking-[0.15em] uppercase transition-colors"
              data-testid="admin-back-home"
            >
              <ArrowLeft size={14} /> Naar website
            </a>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-cgprimary hover:bg-cgprimary px-4 py-2.5 text-[12px] font-semibold tracking-[0.15em] uppercase transition-all"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Uitloggen
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-cgdark">Open vacatures</h1>
            <p className="text-cgmuted text-sm mt-1">{vacancies.length} vacature(s) totaal</p>
          </div>
          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 bg-cgprimary hover:bg-cgprimaryhover text-white px-5 py-3 text-[12px] font-semibold tracking-[0.15em] uppercase transition-all"
            data-testid="admin-new-vacancy"
          >
            <Plus size={16} /> Nieuwe vacature
          </button>
        </div>

        {loading ? (
          <div className="text-cgmuted text-sm">Laden...</div>
        ) : vacancies.length === 0 ? (
          <div className="bg-white border border-cgborder p-10 text-center text-cgmuted" data-testid="admin-empty">
            Nog geen vacatures. Klik op "Nieuwe vacature" om er één toe te voegen.
          </div>
        ) : (
          <div className="space-y-3">
            {vacancies.map((v) => (
              <div
                key={v.id}
                className="bg-white border border-cgborder p-5 flex items-center justify-between gap-4"
                data-testid={`admin-vacancy-${v.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold tracking-[0.15em] uppercase ${
                        v.active ? "bg-cgprimary text-white" : "bg-cgborder text-cgdark"
                      }`}
                    >
                      {v.active ? "Live" : "Verborgen"}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-cgmuted font-semibold">
                      {JOB_TYPE_OPTIONS.find((o) => o.value === v.type_job)?.label || v.type_job}
                    </span>
                  </div>
                  <div className="font-heading text-lg font-semibold text-cgdark truncate">{v.title}</div>
                  {v.subtitle && <div className="text-cgmuted text-sm truncate">{v.subtitle}</div>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleActive(v)}
                    className="w-9 h-9 flex items-center justify-center border border-cgborder hover:border-cgprimary text-cgdark hover:text-cgprimary transition-colors"
                    title={v.active ? "Verbergen" : "Live zetten"}
                    data-testid={`admin-toggle-${v.id}`}
                  >
                    {v.active ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button
                    onClick={() => openEdit(v)}
                    className="w-9 h-9 flex items-center justify-center border border-cgborder hover:border-cgprimary text-cgdark hover:text-cgprimary transition-colors"
                    data-testid={`admin-edit-${v.id}`}
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    onClick={() => remove(v)}
                    className="w-9 h-9 flex items-center justify-center border border-cgborder hover:border-red-500 text-cgdark hover:text-red-500 transition-colors"
                    data-testid={`admin-delete-${v.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-form-modal">
          <div className="bg-white w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-cgborder">
              <h2 className="font-heading text-xl font-semibold text-cgdark">
                {editingId ? "Vacature bewerken" : "Nieuwe vacature"}
              </h2>
              <button onClick={() => setShowForm(false)} className="w-9 h-9 flex items-center justify-center text-cgdark hover:text-cgprimary" data-testid="admin-form-close">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={save} className="p-5 space-y-5">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-cgmuted font-semibold mb-2">Titel *</label>
                <input
                  className="cg-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="bv. Student poetshulp"
                  required
                  data-testid="admin-input-title"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-cgmuted font-semibold mb-2">Ondertitel</label>
                <input
                  className="cg-input"
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  placeholder="Optioneel"
                  data-testid="admin-input-subtitle"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-cgmuted font-semibold mb-2">Type *</label>
                <select
                  className="cg-input cg-select"
                  value={form.type_job}
                  onChange={(e) => setForm({ ...form, type_job: e.target.value })}
                  data-testid="admin-input-type"
                >
                  {JOB_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-cgmuted font-semibold mb-2">Beschrijving *</label>
                <textarea
                  className="cg-input min-h-[100px] resize-y"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Korte omschrijving van de job"
                  required
                  data-testid="admin-input-description"
                />
              </div>

              {/* Highlights */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] uppercase tracking-[0.2em] text-cgmuted font-semibold">Highlights (kernpunten)</label>
                  <button type="button" onClick={() => addListItem("highlights")} className="text-cgprimary text-xs font-semibold hover:underline">+ Toevoegen</button>
                </div>
                <div className="space-y-2">
                  {form.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        className="cg-input flex-1 !py-2"
                        value={h}
                        onChange={(e) => updateList("highlights", i, e.target.value)}
                        placeholder="bv. Voltijds · Ma-vrij"
                      />
                      <button type="button" onClick={() => removeListItem("highlights", i)} className="text-cgmuted hover:text-red-500 p-2">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tasks */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] uppercase tracking-[0.2em] text-cgmuted font-semibold">Taken lijst</label>
                  <button type="button" onClick={() => addListItem("tasks")} className="text-cgprimary text-xs font-semibold hover:underline">+ Toevoegen</button>
                </div>
                <div className="space-y-2">
                  {form.tasks.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        className="cg-input flex-1 !py-2"
                        value={t}
                        onChange={(e) => updateList("tasks", i, e.target.value)}
                        placeholder="bv. Bureau stofzuigen en dweilen"
                      />
                      <button type="button" onClick={() => removeListItem("tasks", i)} className="text-cgmuted hover:text-red-500 p-2">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-5 h-5 accent-cgprimary"
                  data-testid="admin-input-active"
                />
                <span className="text-sm text-cgdark font-medium">Live zetten op de website</span>
              </label>

              {error && <div className="border-l-2 border-red-500 bg-red-50 p-3 text-red-700 text-sm">{error}</div>}

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-cgborder">
                <button type="button" onClick={() => setShowForm(false)} className="cg-btn-ghost">Annuleer</button>
                <button type="submit" className="cg-btn-primary" data-testid="admin-form-submit">
                  <Check size={16} /> {editingId ? "Opslaan" : "Aanmaken"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
